<template>
  <div class="field box py-1">
    <div class="field-label is-normal is-flex is-justify-content-space-between is-align-items-center m-0 mb-3 is-flex-direction-row-reverse">
      <button v-if="submitableButton && edit" class="button is-info" @click="handleChange">Submit</button>
      <!--
        N10 / VA1 - who last wrote this abstract, and when.
        Muted and small on purpose - it is provenance, not content.
      -->
      <div class="is-flex is-align-items-baseline">
        <label class="label m-0">Abstract</label>
        <span v-if="updatedLabel" class="is-size-7 has-text-grey-light ml-2" dir="rtl" data-test="abstract-provenance">{{ updatedLabel }}</span>
      </div>
    </div>
    <!--
      VA1 - Quill is mounted ONLY after the recruiter clicks to edit. Mounting it
      hidden on modal open used to flatten the chatbot's plain-text abstract and
      auto-save it under the recruiter's name.
    -->
    <div v-if="edit" class="control ql-container-custom">
      <quill output="html" :config="quillConfig" ref="quillEditor" @input="handleTextChange" @hook:mounted="onEditorMounted" />
    </div>
    <div v-else @click="updateEdit" data-test="abstract-view">
      <div dir="rtl" v-if="text">
        <div class="is-clickable break_word has-text-link" :class="{ white_space_pre_line: !textIsHtml }" v-html="$sanitize(text)"></div>
      </div>
      <div v-else class="is-clickable">
        Create...
      </div>
    </div>
    <!-- Chat handoff summary: read-only plain text, never part of the abstract / never saved. -->
    <div v-if="showHandoffSummary" class="mt-2 mb-2" dir="rtl" data-test="chatbot-handoff-summary">
      <a class="is-size-7 has-text-grey" @click.prevent="handoffOpen = !handoffOpen" data-test="chatbot-handoff-toggle">
        {{ handoffOpen ? '▾' : '◂' }} {{ handoffTitle }}
      </a>
      <div v-if="handoffOpen" class="is-size-7 has-text-grey-dark break_word mt-1 white_space_pre_line" data-test="chatbot-handoff-text">{{ chatbotHandoffSummary.text }}</div>
      <a v-if="handoffOpen && handoffConversationUrl" class="is-size-7 has-text-link" :href="handoffConversationUrl" target="_blank" rel="noopener" data-test="chatbot-handoff-link">לשיחה</a>
    </div>
    <!-- VA1 - the chatbot's own abstract, when it differs from the current one. Read-only, click to expand. -->
    <div v-if="showChatbotAbstract" class="mt-2 mb-2" dir="rtl" data-test="chatbot-abstract">
      <a class="is-size-7 has-text-grey" @click.prevent="chatbotOpen = !chatbotOpen">
        {{ chatbotOpen ? '▾' : '◂' }} {{ chatbotAbstractTitle }}
      </a>
      <div v-if="chatbotOpen" class="is-size-7 has-text-grey-dark break_word mt-1" :class="{ white_space_pre_line: !chatbotIsHtml }" v-html="$sanitize(chatbotText)" data-test="chatbot-abstract-text"></div>
    </div>
  </div>
</template>

<script>
import { isHtml, toEditorHtml, abstractFingerprint } from '@/custom/helpers/abstract_text'
import { formatDate } from '@/custom/helpers/format_date'

// Recruiter edits are saved 800 ms after the last keystroke, on blur, on
// Submit and when the editor closes - never per keystroke.
export const ABSTRACT_SAVE_DEBOUNCE_MS = 800

export default {
  name: 'ApplicantAbstract',
  props: {
    value: {
      type: [String],
      validator: prop => typeof prop === 'string' || prop === null
    },
    applicantId: {
      type: Number,
      required: true
    },
    submitableButton: {
      type: Boolean,
      default: true
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    // N10 - applicants.abstract_updated_at / abstract_updated_by, straight off
    // the admin applicant payload. Null on an older backend -> nothing rendered.
    updatedAt: {
      type: String,
      default: null
    },
    updatedBy: {
      type: String,
      default: null
    },
    // R1.4 - server-resolved display name of the actor ("צ'אטבוט", admin name,
    // "מערכת"). Absent on an older backend -> fall back to the N10 label.
    updatedByLabel: {
      type: String,
      default: null
    },
    // VA1 - the chatbot's generated abstract: {content, generated_at, version,
    // generated_by} from applicants#show (a plain string is tolerated too).
    chatbotAbstract: {
      type: [String, Object],
      default: null
    },
    chatbotAbstractGeneratedAt: {
      type: String,
      default: null
    },
    // {text, conversation_id, at} or null
    chatbotHandoffSummary: {
      type: Object,
      default: null
    }
  },
  computed: {
    // 'chatbot' is the literal Applicant::ABSTRACT_ACTOR_CHATBOT the bridge
    // stamps; anything else is a person's name.
    isChatbotAbstract () {
      return this.updatedBy === 'chatbot'
    },
    updatedLabel () {
      const parts = []
      const stamp = formatDate(this.updatedAt, 'DD/MM/YYYY HH:mm')
      if (stamp) parts.push(`עודכן ${stamp}`)
      if (this.updatedByLabel) parts.push(`על ידי ${this.updatedByLabel}`)
      else if (this.isChatbotAbstract) parts.push('נוצר בשיחה עם הצ\'אטבוט')
      return parts.join(' · ')
    },
    textIsHtml () {
      return isHtml(this.text)
    },
    chatbotText () {
      const c = this.chatbotAbstract
      if (!c) return ''
      if (typeof c === 'string') return c
      return c.content || c.text || c.abstract || ''
    },
    chatbotIsHtml () {
      return isHtml(this.chatbotText)
    },
    chatbotGeneratedAt () {
      const c = this.chatbotAbstract
      return (c && typeof c === 'object' && c.generated_at) || this.chatbotAbstractGeneratedAt || null
    },
    showChatbotAbstract () {
      const fp = abstractFingerprint(this.chatbotText)
      return fp !== '' && fp !== abstractFingerprint(this.text)
    },
    showHandoffSummary () {
      const h = this.chatbotHandoffSummary
      return !!(h && typeof h.text === 'string' && h.text.trim() !== '')
    },
    handoffTitle () {
      const stamp = formatDate(this.chatbotHandoffSummary && this.chatbotHandoffSummary.at, 'DD.MM.YYYY')
      return stamp ? `סיכום שיחת צ'אט (${stamp})` : 'סיכום שיחת צ\'אט'
    },
    handoffConversationUrl () {
      const id = this.chatbotHandoffSummary && this.chatbotHandoffSummary.conversation_id
      return id ? `https://conversations.hasama.online/panel/conversation/${encodeURIComponent(id)}` : null
    },
    chatbotAbstractTitle () {
      let title = 'תקציר המערכת (צ\'אטבוט)'
      const stamp = formatDate(this.chatbotGeneratedAt, 'DD/MM/YYYY HH:mm')
      if (stamp) title += ` · ${stamp}`
      return title
    }
  },
  data () {
    return {
      edit: false,
      text: '',
      // HTML the editor held right after it was loaded / last saved. Any
      // text-change equal to it is programmatic (mount, alignment) -> no save.
      lastSaved: null,
      // debounced save state: the HTML waiting to be sent, its timer, the
      // request chain (one PUT at a time, in order) and a sequence number so
      // a stale response never acts.
      pendingHtml: null,
      saveTimer: null,
      saveChain: Promise.resolve(),
      saveSeq: 0,
      chatbotOpen: false,
      handoffOpen: false,
      quillConfig: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ direction: 'rtl' }]
          ]
        }
      }
    }
  },
  watch: {
    text (newVal) {
      this.$emit('input', newVal)
    },
    value (newVal) {
      // The parent loads the applicant asynchronously; follow it while the
      // recruiter is not editing.
      if (!this.edit && newVal !== this.text) this.text = newVal
    }
  },
  created () {
    this.text = this.value
    this.edit = this.isEditable
    this.addQuillStyling()
  },
  beforeDestroy () {
    this.flushSave()
    this.detachQuillBlurEvent()
  },
  methods: {
    addQuillStyling () {
      if (document.querySelector('link[data-quill-snow]')) return
      var link = document.createElement('link')
      link.setAttribute('rel', 'stylesheet')
      link.setAttribute('data-quill-snow', '1')
      link.setAttribute('href', 'https://cdn.quilljs.com/1.2.6/quill.snow.css')
      document.body.appendChild(link)
    },
    updateEdit () {
      this.edit = true
    },
    // Runs once the vue-quill child has created its Quill instance. Loads the
    // abstract (plain text converted to paragraphs), applies RTL alignment and
    // flushes those DOM changes SILENTLY so nothing is emitted or saved.
    onEditorMounted () {
      const cmp = this.$refs.quillEditor
      const quill = cmp && cmp.editor
      if (!quill || !quill.root) return
      const plain = !isHtml(this.text)
      // Stored HTML is untrusted (chatbot / candidate text): run it through the
      // same allowlist as view mode before it touches the live DOM.
      const safe = plain ? (this.text || '') : this.$sanitize(this.text)
      quill.root.innerHTML = toEditorHtml(safe) || '<p><br></p>'
      const paragraphs = quill.root.querySelectorAll('p')
      const targets = plain ? Array.prototype.slice.call(paragraphs) : (paragraphs[0] ? [paragraphs[0]] : [])
      targets.forEach(p => {
        p.classList.add('ql-align-right')
        p.classList.add('ql-direction-rtl')
      })
      const container = cmp.$el.querySelector('.ql-container')
      if (container) container.classList.add('ql-container-custom')
      quill.update('silent')
      if (quill.history && quill.history.clear) quill.history.clear()
      this.lastSaved = quill.root.innerHTML
      this.attachQuillBlurEvent()
    },
    attachQuillBlurEvent () {
      const quillEditor = this.$refs.quillEditor && this.$refs.quillEditor.editor
      if (quillEditor && quillEditor.on) {
        quillEditor.on('selection-change', (range) => {
          if (range === null) this.flushSave()
        })
      }
    },
    detachQuillBlurEvent () {
      const quillEditor = this.$refs.quillEditor && this.$refs.quillEditor.editor
      if (quillEditor && quillEditor.off) {
        quillEditor.off('selection-change')
      }
    },
    handleTextChange (newText) {
      // Not loaded yet -> programmatic.
      if (this.lastSaved === null) return
      this.text = newText
      if (newText === this.lastSaved) {
        // back to what is stored (or a programmatic no-op): nothing to send
        this.pendingHtml = null
        this.clearSaveTimer()
        return
      }
      this.pendingHtml = newText
      this.clearSaveTimer()
      this.saveTimer = setTimeout(() => this.flushSave(), ABSTRACT_SAVE_DEBOUNCE_MS)
    },
    clearSaveTimer () {
      if (this.saveTimer) {
        clearTimeout(this.saveTimer)
        this.saveTimer = null
      }
    },
    flushSave () {
      this.clearSaveTimer()
      if (this.pendingHtml === null) return this.saveChain
      const html = this.pendingHtml
      this.pendingHtml = null
      this.lastSaved = html
      const seq = ++this.saveSeq
      this.saveChain = this.saveChain.then(() => this.putAbstract(html, seq))
      return this.saveChain
    },
    async putAbstract (html, seq) {
      try {
        await this.$network_utils.updateApplicant({
          applicant_id: this.applicantId,
          abstract: html,
          active_at: new Date()
        })
      } catch (e) {
        // only the newest edit decides; re-queue it so the next blur/Submit retries
        if (seq === this.saveSeq && this.pendingHtml === null) {
          this.pendingHtml = html
          // undefined (not null): edits keep flowing, nothing counts as saved
          this.lastSaved = undefined
          this.$utils.emit('show_toast', { text: 'שמירת התקציר נכשלה - נסו שוב' })
        }
      }
    },
    async handleChange () {
      this.flushSave()
      this.detachQuillBlurEvent()
      this.edit = false
      this.lastSaved = null
      this.handleStatCreation()
      this.$utils.emit('show_toast', { text: 'Success' })
    },
    handleStatCreation () {
      this.$network_utils.postStat({
        title: 'new internal note',
        designator: 'internal_notes',
        content: this.text,
        status: 'done',
        owner: this.$utils.getOwner().name,
        priority: 7,
        resource_id: this.applicantId,
        resource_type: 'Applicant'
      })
    }
  }
}
</script>

<style>
  .ql-container-custom {
    font-size: 17px !important;
  }
</style>
